import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const Designation = () => {
    const [designations, setDesignations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 15,
        hasNextPage: false,
        hasPreviousPage: false
    });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', or 'all'
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [designationToDelete, setDesignationToDelete] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingDesignationId, setEditingDesignationId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        status: 'active'
    });
    const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, search, statusFilter]);

    const fetchDesignations = async () => {
        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'get-designations',
                page: pagination.currentPage,
                limit: pagination.limit,
                search: search || '',
                ...(statusFilter !== 'all' && { status: statusFilter })
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                setDesignations(result.data || []);
                if (result.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        currentPage: result.pagination.currentPage || prev.currentPage,
                        totalPages: result.pagination.totalPages || prev.totalPages,
                        totalCount: result.pagination.total || prev.totalCount,
                        hasNextPage: result.pagination.hasNextPage || false,
                        hasPreviousPage: result.pagination.hasPreviousPage || false
                    }));
                }
            } else {
                toast.error(result?.message || 'Failed to fetch designations');
            }
        } catch (error) {
            console.error('Error fetching designations:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleStatusFilter = (e) => {
        const value = e.target.value;
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const getSerialNumber = (index) => {
        return (pagination.currentPage - 1) * pagination.limit + index + 1;
    };

    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase();
        let className = 'employee-status';
        let text = status?.toUpperCase() || 'ACTIVE';

        switch (statusLower) {
            case 'active':
                className += ' employee-status-active';
                text = 'ACTIVE';
                break;
            case 'inactive':
                className += ' employee-status-inactive';
                text = 'INACTIVE';
                break;
            default:
                className += ' employee-status-active';
                text = 'ACTIVE';
        }

        return <span className={className}>{text}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const handleAdd = () => {
        setIsEditMode(false);
        setEditingDesignationId(null);
        setFormData({
            name: '',
            status: 'active'
        });
        setFormErrors({});
        setShowAddModal(true);
    };

    const handleEdit = (designation) => {
        setIsEditMode(true);
        setEditingDesignationId(designation.id);
        setFormData({
            name: designation.name || '',
            status: designation.status || 'active'
        });
        setFormErrors({});
        setShowAddModal(true);
    };

    const handleDelete = (designation) => {
        setDesignationToDelete(designation);
        setShowDeleteModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setIsEditMode(false);
        setEditingDesignationId(null);
        setFormData({
            name: '',
            status: 'active'
        });
        setFormErrors({});
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDesignationToDelete(null);
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name || !formData.name.trim()) {
            errors.name = 'Designation name is required';
        } else if (formData.name.trim().length < 2) {
            errors.name = 'Designation name must be at least 2 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const currentUserId = localStorage.getItem('id');

            if (isEditMode) {
                // Update designation
                const payload = {
                    action: 'update-designation',
                    id: editingDesignationId,
                    name: formData.name.trim(),
                    status: formData.status || 'active',
                    ...(currentUserId && { updatedBy: currentUserId })
                };

                const response = await axios.post(API_URL, payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                const result = response.data;

                if (result?.success) {
                    toast.success('Updated successfully');
                    closeAddModal();
                    fetchDesignations();
                } else {
                    toast.error(result?.message || 'Failed to update designation');
                }
            } else {
                // Add designation
                const payload = {
                    action: 'add-designation',
                    name: formData.name.trim(),
                    status: 'active',
                    ...(currentUserId && { createdBy: currentUserId })
                };

                const response = await axios.post(API_URL, payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                const result = response.data;

                if (result?.success) {
                    toast.success('Created successfully');
                    closeAddModal();
                    fetchDesignations();
                } else {
                    toast.error(result?.message || 'Failed to create designation');
                }
            }
        } catch (error) {
            console.error('Error saving designation:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);

            // Handle specific error cases
            if (error.response?.status === 409) {
                setFormErrors({ name: 'Designation name already exists' });
            }
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!designationToDelete) return;

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        const updatedBy = localStorage.getItem('id');

        setLoading(true);
        try {
            const payload = {
                action: 'delete-designation',
                id: designationToDelete.id,
                ...(updatedBy && { updatedBy: updatedBy })
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                toast.success('Deleted');
                closeDeleteModal();
                fetchDesignations();
            } else {
                toast.error(result?.message || 'Failed to delete designation');
            }
        } catch (error) {
            console.error('Error deleting designation:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    {/* Header with Title and Add Button */}
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-8">
                            <h1 className="employee-title">
                                Manage Designations
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                Manage designations for your organization by adding, updating, or deleting designation entries.
                            </p>
                        </div>
                        <div className="col-md-4 employee-view-header-actions">
                            <button type="button" className="employee-back-btn" onClick={handleAdd}>
                                Add Designation
                            </button>
                        </div>
                    </div>

                    {/* Designation Table Card */}
                    <div className="card employee-content-card">
                        <div className="card-body">
                            <div className="employee-table-container">
                                <div className="employee-search-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                                    <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '200px' }}>
                                        <input
                                            type="text"
                                            className="form-input employee-search-input"
                                            placeholder="Search designations..."
                                            value={search}
                                            onChange={handleSearch}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label htmlFor="statusFilter" className="form-label" style={{ marginBottom: '5px', fontSize: '12px', color: '#6b7280', display: 'block' }}>
                                            Status
                                        </label>
                                        <select
                                            id="statusFilter"
                                            className="form-input"
                                            value={statusFilter}
                                            onChange={handleStatusFilter}
                                            style={{
                                                padding: '8px 12px',
                                                fontSize: '14px',
                                                height: '40px',
                                                width: '150px'
                                            }}
                                        >
                                            <option value="all">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>

                                {loading ? (
                                    <Loading size={120} />
                                ) : (
                                    <>
                                        <div className="employee-table-wrapper">
                                            <table className="employee-table">
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>Designation Name</th>
                                                        <th>Status</th>
                                                        <th>Created At</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {designations.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="5" className="employee-empty">
                                                                No designations found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        designations.map((designation, index) => (
                                                            <tr key={designation.id}>
                                                                <td>{getSerialNumber(index)}</td>
                                                                <td>{designation.name || '-'}</td>
                                                                <td>{getStatusBadge(designation.status)}</td>
                                                                <td>{formatDate(designation.createdAt)}</td>
                                                                <td>
                                                                    <div className="employee-actions">
                                                                        <button
                                                                            className="employee-action-btn employee-action-edit"
                                                                            onClick={() => handleEdit(designation)}
                                                                            title="Edit"
                                                                        >
                                                                            <i className="fa-solid fa-pencil"></i>
                                                                        </button>
                                                                        <button
                                                                            className="employee-action-btn employee-action-delete"
                                                                            onClick={() => handleDelete(designation)}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="fa-solid fa-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {designations.length > 0 && pagination.totalPages > 0 && (
                                            <div className="employee-pagination">
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={pagination.currentPage === 1}
                                                >
                                                    «
                                                </button>
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                    disabled={pagination.currentPage === 1}
                                                >
                                                    ‹
                                                </button>
                                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                                    <button
                                                        key={page}
                                                        className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                                                        onClick={() => handlePageChange(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                ))}
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                    disabled={pagination.currentPage === pagination.totalPages}
                                                >
                                                    ›
                                                </button>
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(pagination.totalPages)}
                                                    disabled={pagination.currentPage === pagination.totalPages}
                                                >
                                                    »
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={closeAddModal}>
                    <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{isEditMode ? 'Edit Designation' : 'Add Designation'}</h3>
                            <button className="modal-close" onClick={closeAddModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="name" className="form-label">
                                        Designation Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        className={`form-input ${formErrors.name ? 'error' : ''}`}
                                        placeholder="Enter designation name"
                                        value={formData.name}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, name: e.target.value }));
                                            if (formErrors.name) {
                                                setFormErrors(prev => ({ ...prev, name: '' }));
                                            }
                                        }}
                                        required
                                    />
                                    {formErrors.name && (
                                        <span className="error-message">{formErrors.name}</span>
                                    )}
                                </div>

                                {isEditMode && (
                                    <div className="form-group">
                                        <label htmlFor="status" className="form-label">
                                            Status <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="status"
                                            className={`form-input ${formErrors.status ? 'error' : ''}`}
                                            value={formData.status}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, status: e.target.value }));
                                                if (formErrors.status) {
                                                    setFormErrors(prev => ({ ...prev, status: '' }));
                                                }
                                            }}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        {formErrors.status && (
                                            <span className="error-message">{formErrors.status}</span>
                                        )}
                                    </div>
                                )}

                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeAddModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update' : 'Add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={closeDeleteModal}>
                    <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button className="modal-close" onClick={closeDeleteModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete <strong>{designationToDelete?.name} Designation</strong>?</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-primary" onClick={confirmDelete}>
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Designation;
